import { fmtDate } from "../lib/date";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMyActions } from "../context/MyActionsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { OperationalDashboard } from "./DashboardPage";
import { AssetAcceptance } from "../components/AssetAcceptance";
import {
  Truck, WarningOctagon, GraduationCap, Bell, ArrowRight, ShieldWarning, HardHat, SealCheck, Gauge, UserCircle,
} from "@phosphor-icons/react";

const TONES = {
  slate: "bg-slate-50 border-slate-200", blue: "bg-blue-50 border-blue-200",
  orange: "bg-orange-50 border-orange-200", red: "bg-red-50 border-red-200",
  amber: "bg-amber-50 border-amber-200",
};
const ICON_TONE = {
  slate: "text-slate-500", blue: "text-blue-600", orange: "text-orange-600", red: "text-red-600", amber: "text-amber-600",
};

function Section({ title, Icon, count, children, tone = "slate", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" data-testid={testid || `home-section-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${TONES[tone]}`}>
        <Icon size={18} weight="bold" className={ICON_TONE[tone]} />
        <h3 className="font-display text-base font-bold text-slate-800">{title}</h3>
        {count != null && count > 0 && <span className={`ml-auto font-mono text-xs font-bold px-2 py-0.5 rounded ${tone === "slate" ? "bg-slate-200 text-slate-700" : `${ICON_TONE[tone]} bg-white border ${TONES[tone].split(" ")[1]}`}`}>{count}</span>}
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

const empty = <p className="text-sm text-slate-400 px-2 py-3">Nothing right now.</p>;
const rowCls = "flex items-center justify-between gap-2 px-2 py-2 border-b last:border-0 border-slate-100 rounded hover:bg-slate-50 transition-colors duration-150";

function AttentionChip({ label, value, tone, to }) {
  const chip = {
    red: "bg-red-600 text-white", amber: "bg-amber-400 text-slate-900", orange: "bg-orange-500 text-white", slate: "bg-slate-900 text-white",
  }[tone];
  const inner = (
    <div className={`rounded-md px-3 py-2.5 ${chip} flex flex-col`}>
      <span className="font-display text-2xl font-black leading-none">{value}</span>
      <span className="text-[11px] uppercase tracking-wide font-semibold mt-1 opacity-90">{label}</span>
    </div>
  );
  return to ? <Link to={to} data-testid={`home-chip-${label.toLowerCase().replace(/ /g, "-")}`} className="block hover:-translate-y-px transition-transform duration-150">{inner}</Link> : inner;
}

function MyActions({ data, name }) {
  const attention = [
    { label: "Return to Service", value: data?.rts_required?.length || 0, tone: "orange" },
    { label: "RAMS to Sign", value: data?.rams_outstanding?.length || 0, tone: "red" },
    { label: "PPE Actions", value: data?.ppe_attention?.length || 0, tone: "amber" },
    { label: "Training Due", value: data?.training_attention?.length || 0, tone: "amber" },
  ].filter((a) => a.value > 0);

  return (
    <div className="flex flex-col gap-4">
      <AssetAcceptance />
      {attention.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="home-attention-strip">
          {attention.map((a) => <AttentionChip key={a.label} {...a} />)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.rts_required?.length > 0 && (
          <Section title="Return to Service Required" Icon={SealCheck} count={data.rts_required.length} tone="orange">
            {data.rts_required.map((a) => (
              <Link key={a.id} to={`/assets/${a.id}`} data-testid={`home-rts-${a.id}`} className={rowCls}>
                <div>
                  <p className="font-mono text-xs font-bold uppercase tracking-wide text-orange-700">#{a.asset_number} {a.asset_name}{a.registration ? ` · ${a.registration}` : ""}</p>
                  <p className="font-body text-sm text-slate-700">Repair completed — review &amp; return to service</p>
                </div>
                <ArrowRight size={16} className="text-slate-400 shrink-0" />
              </Link>
            ))}
          </Section>
        )}

        <Section title="Assigned Assets" Icon={Truck} count={data?.assigned_assets?.length}>
          {data?.assigned_assets?.length ? data.assigned_assets.map((a) => (
            <Link key={a.id} to={`/assets/${a.id}`} data-testid={`home-asset-${a.id}`} className={rowCls}>
              <span className="font-body text-slate-800 text-sm"><span className="font-mono font-bold text-slate-500">{a.asset_number}</span> · {a.name}</span>
              <ArrowRight size={16} className="text-slate-400 shrink-0" />
            </Link>
          )) : empty}
        </Section>

        <Section title="Faults I Reported" Icon={WarningOctagon} count={data?.my_open_faults?.length}>
          {data?.my_open_faults?.length ? data.my_open_faults.map((f) => (
            <div key={f.id} data-testid={`home-fault-${f.id}`} className="px-2 py-2 border-b last:border-0 border-slate-100">
              {(f.asset_number || f.asset_name) && (
                <p className="font-mono text-xs font-bold uppercase tracking-wide text-blue-700">
                  {f.asset_number ? `#${f.asset_number} ` : ""}{f.asset_name}{f.asset_registration ? ` · ${f.asset_registration}` : ""}
                </p>
              )}
              <p className="font-body text-slate-800 text-sm">{f.title || f.description}</p>
            </div>
          )) : empty}
        </Section>

        <Section title="Documents to Acknowledge" Icon={ShieldWarning} count={data?.rams_outstanding?.length} tone={data?.rams_outstanding?.length ? "red" : "slate"}>
          {data?.rams_outstanding?.length ? data.rams_outstanding.map((r) => (
            <Link key={r.id} to="/rams" className={rowCls}>
              <span className="font-body text-slate-800 text-sm">{r.ref} · {r.title} <span className="font-mono text-xs text-slate-500">Rev {r.revision}</span></span>
              <span className="text-xs font-bold uppercase bg-red-600 text-white px-2 py-0.5 rounded shrink-0">Sign</span>
            </Link>
          )) : empty}
        </Section>

        <Section title="PPE Attention" Icon={HardHat} count={data?.ppe_attention?.length} tone={data?.ppe_attention?.length ? "amber" : "slate"}>
          {data?.ppe_attention?.length ? data.ppe_attention.map((p) => (
            <Link key={p.id} to="/ppe" className={rowCls}>
              <span className="font-body text-slate-800 text-sm">{p.ppe_name}</span>
              <span className="text-xs font-bold uppercase bg-amber-400 text-slate-900 px-2 py-0.5 rounded shrink-0">{p.reason.replace(/_/g, " ")}</span>
            </Link>
          )) : empty}
        </Section>

        <Section title="Training Attention" Icon={GraduationCap} count={data?.training_attention?.length} tone={data?.training_attention?.length ? "amber" : "slate"}>
          {data?.training_attention?.length ? data.training_attention.map((t) => (
            <div key={t.id} className={rowCls}>
              <span className="font-body text-slate-800 text-sm">{t.competency_name}</span>
              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded shrink-0 ${t.state === "expired" ? "bg-red-600 text-white" : "bg-amber-400 text-slate-900"}`}>
                {t.state}{t.expiry_date ? ` · ${fmtDate(t.expiry_date)}` : ""}
              </span>
            </div>
          )) : empty}
        </Section>

        <Section title="Notices" Icon={Bell} count={data?.notifications?.length}>
          {data?.notifications?.length ? data.notifications.map((n) => (
            n.type === "havs_exposure" && n.entity_id ? (
              <Link key={n.id} to={`/havs?person=${n.entity_id}`} data-testid={`notice-${n.id}`} className="block px-2 py-2 border-b last:border-0 border-slate-100 hover:bg-slate-50 rounded transition-colors duration-150">
                <p className={`font-body font-semibold text-sm ${n.severity === "critical" ? "text-red-700" : "text-slate-800"}`}>{n.title}</p>
                <p className="font-body text-slate-600 text-xs whitespace-pre-line">{n.body}</p>
              </Link>
            ) : (
              <div key={n.id} data-testid={`notice-${n.id}`} className="px-2 py-2 border-b last:border-0 border-slate-100">
                <p className="font-body font-semibold text-slate-800 text-sm">{n.title}</p>
                <p className="font-body text-slate-600 text-xs">{n.body}</p>
              </div>
            )
          )) : empty}
        </Section>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { profile, hasPerm } = useAuth();
  const { data } = useMyActions();
  const isManager = hasPerm("supervisor", "administrator", "auditor", "asset_manager", "maintenance");

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
          Hello, {profile?.first_name || "there"}
        </h1>
        <p className="font-body text-slate-600 mt-1 text-sm">Here is what needs your attention.</p>
      </div>

      {isManager ? (
        <Tabs defaultValue="mine" className="w-full">
          <TabsList data-testid="home-tabs" className="bg-slate-100 border border-slate-200">
            <TabsTrigger value="mine" data-testid="tab-my-actions" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 font-semibold gap-1.5">
              <UserCircle size={16} weight="bold" /> My Actions
            </TabsTrigger>
            <TabsTrigger value="ops" data-testid="tab-operations" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 font-semibold gap-1.5">
              <Gauge size={16} weight="bold" /> Operations
            </TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-4">
            <MyActions data={data} name={profile?.first_name} />
          </TabsContent>
          <TabsContent value="ops" className="mt-4" data-testid="home-operations">
            <OperationalDashboard />
          </TabsContent>
        </Tabs>
      ) : (
        <MyActions data={data} name={profile?.first_name} />
      )}
    </div>
  );
}
